import { useState, useEffect } from 'react';
import { Heart } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { SavedFavoritesDialog } from '@/components/listings/filters/SavedFavoritesDialog';
import { Link } from '@/lib/router-compat';

interface FavoriteButtonProps {
  listingId: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  /** Current price at the moment of saving — stored so we can show a price diff later */
  price?: number;
}

const FAVORITES_KEY = 'roautomarket_favorites';
const FAVORITES_META_KEY = 'roautomarket_favorites_meta';

export interface FavoriteMeta {
  savedPrice?: number;
  savedAt: string; // ISO date
}

export const getFavoritesMeta = (): Record<string, FavoriteMeta> => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_META_KEY) || '{}');
  } catch {
    return {};
  }
};

const setFavoritesMeta = (meta: Record<string, FavoriteMeta>) => {
  localStorage.setItem(FAVORITES_META_KEY, JSON.stringify(meta));
};

export const FavoriteButton = ({ 
  listingId, 
  title = 'acest anunț',
  size = 'md',
  variant = 'ghost',
  price,
}: FavoriteButtonProps) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const { toast } = useToast();

  // Load favorites from localStorage on mount
  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    setIsFavorite(favorites.includes(listingId));
  }, [listingId]);

  const toggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation if used in a link
    e.stopPropagation();

    const favorites = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    const meta = getFavoritesMeta();
    
    if (isFavorite) {
      // Remove from favorites
      const newFavorites = favorites.filter((id: string) => id !== listingId);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
      delete meta[listingId];
      setFavoritesMeta(meta);
      setIsFavorite(false);
      
      toast({
        title: "Șters din favorite",
        description: `Am șters ${title} din lista ta de favorite.`,
      });
    } else {
      // Add to favorites
      const newFavorites = [...favorites, listingId];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
      meta[listingId] = {
        savedAt: new Date().toISOString(),
        savedPrice: typeof price === 'number' && price > 0 ? price : undefined,
      };
      setFavoritesMeta(meta);
      setIsFavorite(true);
      
      toast({
        title: "Adăugat la favorite ❤️",
        description: (
          <div className="space-y-2">
            <p>{title} a fost adăugat în lista ta de favorite.</p>
            <Link 
              to="/despre-noi#testimoniale" 
              className="text-primary hover:underline text-sm inline-flex items-center gap-1"
            >
              ⭐ Îți place platforma? Lasă-ne un testimonial!
            </Link>
          </div>
        ),
      });
    }
    
    // Trigger custom event for same-tab updates
    window.dispatchEvent(new Event('favoritesChanged'));
  };

  const iconSize = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  }[size];

  return (
    <Button
      variant={variant}
      size={size === 'sm' ? 'sm' : 'icon'}
      onClick={toggleFavorite}
      className={cn(
        "transition-all duration-200",
        isFavorite && "text-red-500 hover:text-red-600"
      )}
      aria-label={isFavorite ? "Șterge din favorite" : "Adaugă la favorite"}
    >
      <Heart 
        className={cn(
          iconSize,
          "transition-all duration-200",
          isFavorite && "fill-current"
        )} 
      />
    </Button>
  );
};

// Hook to get all favorites
export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    const loadFavorites = () => {
      const stored = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      setFavorites(stored);
    };

    loadFavorites();
    
    // Listen for storage changes (from other tabs)
    window.addEventListener('storage', loadFavorites);
    // Also listen for custom event from same tab
    window.addEventListener('favoritesChanged', loadFavorites);
    return () => {
      window.removeEventListener('storage', loadFavorites);
      window.removeEventListener('favoritesChanged', loadFavorites);
    };
  }, []);

  return favorites;
};

// Component to display favorites bar with email option
export const FavoritesBar = () => {
  const favorites = useFavorites();

  if (favorites.length === 0) return null;

  return (
    <div className="sticky top-16 z-40 bg-gradient-to-r from-red-500/10 via-pink-500/10 to-red-500/10 backdrop-blur-sm border-b border-red-200 shadow-xs">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <Link to="/favorite" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Heart className="h-5 w-5 text-red-500 fill-current" />
            <span className="text-sm font-medium">
              {favorites.length} favorite salvate - Vezi toate
            </span>
          </Link>
          <SavedFavoritesDialog favoriteIds={favorites} />
        </div>
      </div>
    </div>
  );
};
